import { isEmpty } from 'underscore';
import React, { useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  LocationTargetSetting,
  isLocationValid,
  getLanguageBundleString
} from '@bingads-webui-campaign/hotel-common-targets';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { isBundledCountriesFound } from '@bingads-webui-campaign-react/search-campaign-data';
import { getLocationsString } from '@bingads-webui-campaign-react/wizard-shared-components';


export const targetOptions = {
  bundle: 'bundle',
  specific: 'specific',
  everywhere: 'everywhere',
};

const getStyles = () =>
  ({
    locationTargeting: {
      '& .radio>label': {
        left: '0',
      },
      '& .location-target-options': {
        '& > ul': {
          paddingLeft: '0',
        },
        '& .intent-target-options': {
          '& > ul': {
            paddingLeft: '20px',
          },
          '& .radio>label': {
            padding: '8px 0 0 0',
          },
        },
      },
    },
  });

export const updateTargetOption = ({
  i18n,
  locations,
  languages,
  market: rawMarket,
  permissions,
  isInit = false,
}) => {
  const { IsDomesticSimplifiedChineseEnabled } = permissions;
  const showEverywhereOption = !IsDomesticSimplifiedChineseEnabled;
  const market = rawMarket || 'US';
  const bundleFound = isBundledCountriesFound({ languages, market });
  const { currentLevelSettings: { targetOption } } = locations;

  const info = i18n.getString(_TL_("Some location settings may be automatically adjusted due to the languages you've selected. Please review your location targeting settings and update as needed."));

  if (targetOption !== targetOptions.specific) {
    const shouldSwitchToBundle = (bundleFound || !showEverywhereOption) && targetOption === targetOptions.everywhere;
    const shouldSwitchToEverywhere = !bundleFound && showEverywhereOption && targetOption === targetOptions.bundle;

    if (shouldSwitchToBundle) {
      if (!isInit) {
        notificationApi.showInfo(info);
      }
      return {
        ...locations,
        currentLevelSettings: {
          ...locations.currentLevelSettings,
          targetOption: targetOptions.bundle,
        },
        useParentLevel: false,
      };
    }

    if (shouldSwitchToEverywhere) {
      if (!isInit) {
        notificationApi.showInfo(info);
      }
      return {
        ...locations,
        currentLevelSettings: {
          ...locations.currentLevelSettings,
          targetOption: targetOptions.everywhere,
        },
      };
    }
  }
  return locations;
};

export const updateLocationTargetsForCampaign = ({
  campaign,
  i18n,
  market: rawMarket,
  permissions,
  isInit = false,
}) => {
  const {
    locationTargets: locations,
    Languages: languages,
  } = campaign;
  const updatedLocations = updateTargetOption({
    i18n,
    locations,
    languages,
    market: rawMarket,
    permissions,
    isInit,
  });

  return {
    ...campaign,
    locationTargets: updatedLocations,
  };
};

const LocationTargetSettingWithCustomValidation = ({
  validationProps, updateValidation, skipValidation, allowBidAdjustment, refreshLocationTargetsTimes, language, market, permissions, ...restProps
}) => {
  const classes = useDefaultStyles(getStyles);
  const { data: locationTargets } = validationProps;
  const i18n = useContext(I18nContext);
  const activity = useContext(ActivityContext);

  const onChange = useCallback((newData) => {
    let errors = [];

    if (!skipValidation) {
      errors = isLocationValid(newData) ? [] : [i18n.getString(_TL_('Location target has validation errors'))];
    }

    updateValidation(errors, { locationTargets: newData });

    if (activity) {
      // signal activity to parent
      activity.signal(`onLocationTargetingChange - ${JSON.stringify(newData)}`);
    }
  }, [skipValidation, updateValidation, activity, i18n]);

  useEffect(() => {
    if (locationTargets && !isEmpty(language)) {
      const newLocations = updateTargetOption({
        i18n,
        locations: locationTargets,
        languages: language,
        market,
        permissions,
      });

      onChange(newLocations);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshLocationTargetsTimes]);

  return (locationTargets ?
    <div className={classnames('location-targeting-section', classes.locationTargeting)}>
      <LocationTargetSetting
        allowBidAdjustment={allowBidAdjustment}
        data={locationTargets}
        setData={onChange}
        showBulkLocations={permissions.dynamic.BulkLocationsTargeting && permissions.dynamic.SearchCampaignCreationRevamp}
        {...restProps}
      />
    </div> : <div />
  );
};

LocationTargetSettingWithCustomValidation.propTypes = {
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      locationTargets: PropTypes.shape({}),
    }),
  }).isRequired,
  refreshLocationTargetsTimes: PropTypes.number.isRequired,
  language: PropTypes.arrayOf(PropTypes.string).isRequired,
  market: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      BulkLocationsTargeting: PropTypes.bool,
      SearchCampaignCreationRevamp: PropTypes.bool,
    }),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  skipValidation: PropTypes.bool,
  allowBidAdjustment: PropTypes.bool,
};

LocationTargetSettingWithCustomValidation.defaultProps = {
  skipValidation: true,
  allowBidAdjustment: false,
};

export const ValidatedLocationTargetSetting = withCustomValidation(LocationTargetSettingWithCustomValidation);

const getLocationDetailsString = (i18n, settings, bundleCountries, isAdGroupLevel, bidStrategyType, hideBid) => {
  const { targetOption } = settings;
  if (targetOption === targetOptions.bundle) {
    return `${i18n.getString(_TL_('Included locations: '))}${getLanguageBundleString(bundleCountries)}`;
  }
  return getLocationsString(i18n, settings, isAdGroupLevel, bidStrategyType, hideBid);
};

export const getLocationTargetTitleDetails = (i18n, location, bundleCountries, isAdGroupLevel, bidStrategyType, hideBid = false) => {
  if (!location) return '';
  const {
    parentLevelSettings,
    currentLevelSettings,
    useParentLevel,
  } = location;

  if (useParentLevel) {
    if (isAdGroupLevel) {
      return i18n.getString(_TL_('Using campaign settings: {{settings}}'), {
        settings: getLocationDetailsString(i18n, parentLevelSettings, bundleCountries, isAdGroupLevel, bidStrategyType, hideBid),
      });
    }
    return i18n.getString(_TL_('Targeting everywhere.'));
  }

  return getLocationDetailsString(i18n, currentLevelSettings, bundleCountries, isAdGroupLevel, bidStrategyType, hideBid);
};
